import { useEffect, useRef, useState } from 'react';

// The client half of the split analysis.
//
// An analysis returns as soon as the holdings are known, carrying a handle for
// the figures YCharts is still calculating. Measured live, that calculation
// took 35 and 59 seconds on two runs of the same statement, which is well past
// what Static Web Apps will hold a proxied request open for. So the waiting
// happens here, in a browser, where a person can watch it and walk away from it,
// rather than inside a request that will be cut off.
//
// Nothing is stored anywhere to make this work: YCharts is running the job and
// its portfolio id is the handle. If the planner closes the tab the figures are
// simply not fetched, and every other number in the report, which is computed
// locally from the extraction, is unaffected.

const ENDPOINT = '/api/AssessmentAnalysisService';
const INTERVAL_MS = 5000;
// Two measured runs took 59s and 35s. Three minutes is generous enough that a
// slow queue does not look like a failure, and short enough that a genuinely
// stuck job stops being polled.
const DEADLINE_MS = 3 * 60 * 1000;

export default function usePortfolioStats(calc) {
  const [state, setState] = useState({ status: 'idle', stats: null });
  const id = calc?.id || null;
  // Held in a ref so a re-render mid-flight cannot restart the clock and give a
  // stuck job an unlimited deadline.
  const startedAt = useRef(0);

  useEffect(() => {
    if (!id) {
      setState({ status: 'idle', stats: null });
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    startedAt.current = Date.now();
    setState({ status: 'calculating', stats: null });

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`${ENDPOINT}?portfolioId=${encodeURIComponent(id)}`);
        if (!res.ok) throw new Error(String(res.status));
        const body = await res.json();
        if (cancelled) return;

        if (body.status === 'ready' || body.status === 'needs_review') {
          setState({ status: body.status, stats: body });
          return;
        }
        if (body.status === 'failed' || body.status === 'unknown') {
          setState({ status: 'failed', stats: null });
          return;
        }
        if (Date.now() - startedAt.current > DEADLINE_MS) {
          setState({ status: 'timeout', stats: null });
          return;
        }
        timer = setTimeout(poll, INTERVAL_MS);
      } catch {
        // A failed poll is not a failed analysis. The rest of the report is
        // already on screen and correct, so this stays quiet.
        if (!cancelled) setState({ status: 'failed', stats: null });
      }
    };

    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [id]);

  return state;
}
